/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { create } from 'zustand';

import { lastDays } from '@/lib/localDate';

export type ThemePreference = 'system' | 'light' | 'dark';

/** The plant the operator is working in, chosen globally from the top bar. */
export interface PlantSelection {
  plantCode: string;
  plantName: string;
  /**
   * EOLT country-plant-line (CPL) used by multi-vehicle MERS / Repair Overview
   * (`GET /vehicles/plants/{cpl}`). Distinct from {@link plantCode} (e.g. AR → 1R1).
   */
  eoltCountryPlantLine?: string;
}

/**
 * Vehicle context shown inline in the top bar while a guided-repair workspace
 * (DTC / MMT) is open. Set on mount, cleared on unmount.
 */
export interface WorkspaceVehicleContext {
  vin?: string;
  modelYear?: string;
  make?: string;
  model?: string;
  pvi?: string;
  csn?: string;
}

/** Walkthrough variant: the whole-app tour or the Schematic Explorer-scoped tour. */
export type TourKind = 'app' | 'explorer';

/**
 * Repair Overview's server-side browse filters. `field` is one of that feature's own
 * `VehicleSearchField` values, kept as a plain string here — shared modules can't import feature
 * types (routes -> features -> shared), and the store never needs to know the exact set.
 */
export interface RepairOverviewFilters {
  field: string;
  term: string;
  from: string;
  to: string;
}

/**
 * Repair Overview's page/filter/expanded-row state. Kept here (rather than local component
 * state) so navigating away to launch a workspace and back doesn't lose the operator's place —
 * combined with the query's own staleTime/gcTime, restoring this state on remount means the
 * matching page comes back from cache instead of another 20-35s round trip.
 */
export interface RepairOverviewBrowseState {
  page: number;
  expandedId: number | null;
  draft: RepairOverviewFilters;
  applied: RepairOverviewFilters;
}

export const NO_REPAIR_OVERVIEW_FILTERS: RepairOverviewFilters = {
  field: 'vin',
  term: '',
  from: '',
  to: '',
};

export const DEFAULT_REPAIR_OVERVIEW_BROWSE: RepairOverviewBrowseState = {
  page: 1,
  expandedId: null,
  draft: NO_REPAIR_OVERVIEW_FILTERS,
  applied: NO_REPAIR_OVERVIEW_FILTERS,
};

/** How many days back the GSIP defects view looks until the operator picks other dates. */
export const GSIP_WINDOW_DAYS = 30;

/**
 * GSIP defects' search: the identifier (`field` is one of that feature's `GsipSearchField`
 * values) and the defect-date window, which starts out as the last {@link GSIP_WINDOW_DAYS} days,
 * today included.
 */
export function gsipDefaultFilters(now: Date = new Date()): RepairOverviewFilters {
  return { field: 'vin', term: '', ...lastDays(GSIP_WINDOW_DAYS, now) };
}

/** GSIP defects' page/search/expanded-row state; kept here for the same reason as Repair Overview's. */
export type GsipBrowseState = RepairOverviewBrowseState;

export function defaultGsipBrowse(now: Date = new Date()): GsipBrowseState {
  const filters = gsipDefaultFilters(now);
  return { page: 1, expandedId: null, draft: filters, applied: filters };
}

/** Active vehicle identity shown in the top bar while the explorer is open. */
export interface VehicleIdentity {
  /** Description for print/header (may fall back to program code when no make/model). */
  name: string;
  vin?: string;
  /** Human vehicle details ("2025 Cadillac Lyriq"), without program code. Safe for all users. */
  details?: string;
  /** Program code, e.g. "C223". Internal-only for display. */
  program?: string;
  /** Engineering / build release, e.g. "APPV". Internal-only for display. */
  release?: string;
  /** Street trim of a launched VIN, e.g. "AT4". Display-only. */
  trim?: string;
}

interface UiState {
  /** Desktop: the rail is widened to name its destinations, moving the page over (persisted). */
  railPinned: boolean;
  setRailPinned: (pinned: boolean) => void;
  /** Tablet: the named rail is open over the page, until the next pick, click elsewhere or Escape. */
  railPeek: boolean;
  setRailPeek: (open: boolean) => void;

  vehicle: VehicleIdentity | null;
  setVehicle: (vehicle: VehicleIdentity | null) => void;

  /** Whether the top-bar vehicle picker dropdown is expanded (lets pages avoid hiding behind it). */
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;

  /** Last visited URL per tool so the nav rail restores URL-driven state. */
  routeMemory: Record<string, string>;
  rememberRoute: (basePath: string, url: string) => void;

  resourcesOpen: boolean;
  openResources: () => void;
  closeResources: () => void;

  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;

  /** Globally-selected plant (persisted locally and mirrored to the user's profile). */
  plant: PlantSelection;
  setPlant: (plant: PlantSelection) => void;

  /** Repair Overview's page/filters/expanded row; see {@link RepairOverviewBrowseState}. */
  repairOverviewBrowse: RepairOverviewBrowseState;
  setRepairOverviewBrowse: (patch: Partial<RepairOverviewBrowseState>) => void;

  /**
   * GSIP defects' page/search/expanded row; see {@link GsipBrowseState}. `null` until the page is
   * first opened, so its date window is computed then rather than when the app loaded.
   */
  gsipBrowse: GsipBrowseState | null;
  setGsipBrowse: (patch: Partial<GsipBrowseState>) => void;

  feedbackOpen: boolean;
  openFeedback: () => void;
  closeFeedback: () => void;

  /** MMT workspace chatbot pane (FAB above feedback opens this). */
  mmtChatOpen: boolean;
  openMmtChat: () => void;
  closeMmtChat: () => void;

  /** DTC workspace chatbot pane (FAB, mirrors {@link mmtChatOpen}). */
  dtcChatOpen: boolean;
  openDtcChat: () => void;
  closeDtcChat: () => void;

  /** Expanded/maximised chat: larger overlay so images render at readable size. */
  chatMaximized: boolean;
  toggleChatMaximized: () => void;
  setChatMaximized: (max: boolean) => void;

  /** Vehicle context rendered in the top bar while a workspace is open. */
  workspaceVehicle: WorkspaceVehicleContext | null;
  setWorkspaceVehicle: (vehicle: WorkspaceVehicleContext | null) => void;

  releaseNotesOpen: boolean;
  openReleaseNotes: () => void;
  closeReleaseNotes: () => void;

  aboutOpen: boolean;
  openAbout: () => void;
  closeAbout: () => void;

  /**
   * Contextual product tour. Whether a tour was already offered is kept per tour by
   * features/tour (`markTourSeen`), not here.
   */
  tourOpen: boolean;
  /** Which tour is active: the page on screen's tour, or the Schematic Explorer tour. */
  tourKind: TourKind;
  startTour: (kind?: TourKind) => void;
  endTour: () => void;
}

const RAIL_PINNED_KEY = 'edx-rail-pinned';

const readRailPinned = (): boolean => {
  try {
    return localStorage.getItem(RAIL_PINNED_KEY) === '1';
  } catch {
    return false;
  }
};

const writeRailPinned = (pinned: boolean) => {
  try {
    localStorage.setItem(RAIL_PINNED_KEY, pinned ? '1' : '0');
  } catch {
    /* storage unavailable */
  }
};

const PLANT_KEY = 'edx-plant';

const EMPTY_PLANT: PlantSelection = { plantCode: '', plantName: '' };

const readPlant = (): PlantSelection => {
  try {
    const raw = localStorage.getItem(PLANT_KEY);
    return raw ? (JSON.parse(raw) as PlantSelection) : EMPTY_PLANT;
  } catch {
    return EMPTY_PLANT;
  }
};

/** Lightweight UI state; server data lives in TanStack Query. Theme persisted to localStorage. */
export const useUiStore = create<UiState>((set) => ({
  railPinned: readRailPinned(),
  setRailPinned: (railPinned) => {
    writeRailPinned(railPinned);
    set({ railPinned });
  },
  railPeek: false,
  setRailPeek: (railPeek) => set({ railPeek }),

  vehicle: null,
  setVehicle: (vehicle) => set({ vehicle }),

  pickerOpen: false,
  setPickerOpen: (pickerOpen) => set({ pickerOpen }),

  routeMemory: {},
  rememberRoute: (basePath, url) =>
    set((s) =>
      s.routeMemory[basePath] === url ? s : { routeMemory: { ...s.routeMemory, [basePath]: url } },
    ),

  resourcesOpen: false,
  openResources: () => set({ resourcesOpen: true }),
  closeResources: () => set({ resourcesOpen: false }),

  theme: (localStorage.getItem('edx-theme') as ThemePreference | null) ?? 'system',
  setTheme: (theme) => {
    try {
      localStorage.setItem('edx-theme', theme);
    } catch {
      /* storage unavailable */
    }
    set({ theme });
  },

  plant: readPlant(),
  setPlant: (plant) => {
    try {
      localStorage.setItem(PLANT_KEY, JSON.stringify(plant));
    } catch {
      /* storage unavailable */
    }
    set({ plant });
  },

  repairOverviewBrowse: DEFAULT_REPAIR_OVERVIEW_BROWSE,
  setRepairOverviewBrowse: (patch) =>
    set((s) => ({ repairOverviewBrowse: { ...s.repairOverviewBrowse, ...patch } })),

  gsipBrowse: null,
  setGsipBrowse: (patch) =>
    set((s) => ({ gsipBrowse: { ...(s.gsipBrowse ?? defaultGsipBrowse()), ...patch } })),

  feedbackOpen: false,
  openFeedback: () => set({ feedbackOpen: true }),
  closeFeedback: () => set({ feedbackOpen: false }),

  mmtChatOpen: false,
  openMmtChat: () => set({ mmtChatOpen: true }),
  closeMmtChat: () => set({ mmtChatOpen: false, chatMaximized: false }),

  dtcChatOpen: false,
  openDtcChat: () => set({ dtcChatOpen: true }),
  closeDtcChat: () => set({ dtcChatOpen: false, chatMaximized: false }),

  chatMaximized: false,
  toggleChatMaximized: () => set((s) => ({ chatMaximized: !s.chatMaximized })),
  setChatMaximized: (chatMaximized) => set({ chatMaximized }),

  workspaceVehicle: null,
  setWorkspaceVehicle: (workspaceVehicle) => set({ workspaceVehicle }),

  releaseNotesOpen: false,
  openReleaseNotes: () => set({ releaseNotesOpen: true }),
  closeReleaseNotes: () => set({ releaseNotesOpen: false }),

  aboutOpen: false,
  openAbout: () => set({ aboutOpen: true }),
  closeAbout: () => set({ aboutOpen: false }),

  tourOpen: false,
  tourKind: 'app',
  startTour: (kind = 'app') => set({ tourOpen: true, tourKind: kind }),
  endTour: () => set({ tourOpen: false }),
}));
